import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import WalletBalance from './components/WalletBalance';
import TransactionHistory from './components/TransactionHistory';
import PayoutRequestForm from './components/PayoutRequestForm';
import AdminApprovalPanel from './components/AdminApprovalPanel';
import EarningsChart from './components/EarningsChart';
import CommissionSummary from './components/CommissionSummary';
import TransactionDetailModal from './components/TransactionDetailModal';
import RefundModal from './components/RefundModal';
import { Wallet, RotateCcw, ShieldCheck, ArrowRight, Menu, X, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';
import { SkeletonList, SkeletonStatCard } from '../../components/ui/SkeletonCard';

const PaymentsWalletScreen = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [balance, setBalance] = useState(0);
  const [pendingBalance, setPendingBalance] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  const [totalDebits, setTotalDebits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [error, setError] = useState('');
  const [payoutSpinner, setPayoutSpinner] = useState(false);
  const [approvalSpinner, setApprovalSpinner] = useState(false);
  const [refundSpinner, setRefundSpinner] = useState(false);
  const [successToast, setSuccessToast] = useState({ visible: false, message: '', subMessage: '' });

  const isAdmin = userProfile?.role === 'admin';
  const isDriver = userProfile?.role === 'driver';
  const currency = wallet?.currency || 'KES';

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user: authUser } } = await supabase?.auth?.getUser();
      setUser(authUser);
      if (authUser) {
        const { data: profile } = await supabase?.from('user_profiles')?.select('*')?.eq('id', authUser?.id)?.maybeSingle();
        setUserProfile(profile);
      }
    };
    fetchUser();
  }, []);

  // Fetch or create wallet
  const fetchWallet = useCallback(async () => {
    if (!user) return;
    try {
      let { data: walletData, error: walletError } = await supabase?.from('wallets')?.select('*')?.eq('user_id', user?.id)?.maybeSingle();

      if (walletError && walletError?.code !== 'PGRST116') {
        console.error('Wallet fetch error:', walletError?.message);
        return;
      }

      // Create wallet if not exists
      if (!walletData) {
        const role = userProfile?.role === 'admin' ? 'platform' : (userProfile?.role || 'customer');
        const { data: newWallet, error: createError } = await supabase?.from('wallets')?.insert({ user_id: user?.id, wallet_role: role, currency: 'KES', status: 'active' })?.select()?.single();
        if (!createError) walletData = newWallet;
      }

      setWallet(walletData);
      return walletData;
    } catch (err) {
      console.error('fetchWallet error:', err?.message);
    }
  }, [user, userProfile]);

  // Compute balance from ledger
  const computeBalance = useCallback(async (walletId) => {
    if (!walletId) return;
    setBalanceLoading(true);
    try {
      const { data, error } = await supabase?.rpc('get_wallet_balance', { p_wallet_id: walletId });
      if (!error) setBalance(parseFloat(data) || 0);

      // Pending (escrow) balance
      const { data: pendingData } = await supabase?.from('wallet_transactions')?.select('amount')?.eq('wallet_id', walletId)?.eq('status', 'pending')?.eq('ledger_type', 'credit');
      const pending = (pendingData || [])?.reduce((sum, tx) => sum + parseFloat(tx?.amount || 0), 0);
      setPendingBalance(pending);
    } catch (err) {
      console.error('computeBalance error:', err?.message);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  // Fetch transactions
  const fetchTransactions = useCallback(async (walletId) => {
    if (!walletId) return;
    try {
      const { data, error } = await supabase?.from('wallet_transactions')?.select('*')?.eq('wallet_id', walletId)?.order('created_at', { ascending: false })?.limit(100);

      if (!error && data) {
        setTransactions(data);
        const credits = data?.filter(tx => tx?.ledger_type === 'credit' && tx?.status === 'completed')?.reduce((sum, tx) => sum + parseFloat(tx?.amount || 0), 0);
        const debits = data?.filter(tx => tx?.ledger_type === 'debit' && tx?.status === 'completed')?.reduce((sum, tx) => sum + parseFloat(tx?.amount || 0), 0);
        setTotalCredits(credits);
        setTotalDebits(debits);
      }
    } catch (err) {
      console.error('fetchTransactions error:', err?.message);
    }
  }, []);

  // Fetch payout requests
  const fetchPayoutRequests = useCallback(async () => {
    if (!user) return;
    try {
      let query = supabase?.from('payout_requests')?.select('*')?.order('created_at', { ascending: false });
      if (!isAdmin) query = query?.eq('driver_id', user?.id);
      const { data, error } = await query;
      if (!error && data) setPayoutRequests(data);
    } catch (err) {
      console.error('fetchPayoutRequests error:', err?.message);
    }
  }, [user, isAdmin]);

  // Initial load
  useEffect(() => {
    if (!user || !userProfile) return;
    const init = async () => {
      setLoading(true);
      let walletData = await fetchWallet();
      if (walletData?.id) {
        await Promise.all([
          computeBalance(walletData?.id),
          fetchTransactions(walletData?.id),
        ]);
      }
      await fetchPayoutRequests();
      setLoading(false);
    };
    init();
  }, [user, userProfile, fetchWallet, computeBalance, fetchTransactions, fetchPayoutRequests]);

  const handleRefresh = async () => {
    if (!wallet?.id) return;
    await Promise.all([
      computeBalance(wallet?.id),
      fetchTransactions(wallet?.id),
      fetchPayoutRequests(),
    ]);
  };

  // Submit payout request
  const handlePayoutSubmit = async ({ amount, bankName, accountNumber, accountName }) => {
    if (!wallet?.id || !user?.id) throw new Error('Wallet not found');
    setPayoutSpinner(true);
    try {
      const { error } = await supabase?.from('payout_requests')?.insert({
          driver_id: user?.id,
          wallet_id: wallet?.id,
          amount,
          status: 'pending',
          bank_name: bankName,
          account_number: accountNumber,
          account_name: accountName,
        });
      if (error) throw new Error(error.message);
      await fetchPayoutRequests();
      setSuccessToast({ visible: true, message: 'Payout request submitted!', subMessage: 'Awaiting admin approval · 24–48 hrs' });
    } finally {
      setPayoutSpinner(false);
    }
  };

  // Admin approve payout — uses approve_payout RPC (atomic, with balance guard)
  const handleApprove = async (requestId) => {
    const request = payoutRequests?.find(r => r?.id === requestId);
    if (!request) throw new Error('Request not found');
    setApprovalSpinner(true);
    try {
      const { data, error } = await supabase?.rpc('approve_payout', {
        p_request_id: requestId,
        p_admin_id: user?.id,
      });

      if (error) throw new Error(error?.message || 'Failed to approve payout');
      if (data?.success === false) throw new Error('Payout approval failed');

      await Promise.all([fetchPayoutRequests(), computeBalance(wallet?.id), fetchTransactions(wallet?.id)]);
      setSuccessToast({ visible: true, message: 'Payout approved!', subMessage: 'Funds released to driver wallet' });
    } finally {
      setApprovalSpinner(false);
    }
  };

  // Admin reject payout
  const handleReject = async (requestId, reason) => {
    const { error } = await supabase?.from('payout_requests')?.update({ status: 'rejected', admin_id: user?.id, processed_at: new Date()?.toISOString(), rejection_reason: reason })?.eq('id', requestId);
    if (error) throw new Error(error.message);
    await fetchPayoutRequests();
    setSuccessToast({ visible: true, message: 'Payout request rejected', subMessage: reason || 'Reason recorded', variant: 'warning' });
  };

  // Admin refund — uses process_refund RPC (atomic, multi-wallet)
  const handleRefund = async ({ orderId, amount, reason, driverWalletId }) => {
    if (!wallet?.id) throw new Error('Wallet not found');
    setRefundSpinner(true);
    try {
      const { data, error } = await supabase?.rpc('process_refund', {
        p_customer_wallet_id: wallet?.id,
        p_platform_wallet_id: wallet?.id,
        p_amount: amount,
        p_order_id: orderId || null,
        p_reason: reason || 'Customer refund',
        p_driver_wallet_id: driverWalletId || null,
      });

      if (error) throw new Error(error?.message || 'Failed to process refund');
      await Promise.all([computeBalance(wallet?.id), fetchTransactions(wallet?.id)]);
      setSuccessToast({ visible: true, message: 'Refund processed!', subMessage: `KES ${amount} returned to customer wallet` });
    } finally {
      setRefundSpinner(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'transactions', label: 'Transactions' },
    ...(isDriver || !isAdmin ? [{ id: 'payout', label: 'Payout' }] : []),
    ...(isAdmin ? [{ id: 'admin', label: 'Admin' }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#060e1e]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Spinner overlays for async operations */}
      <SpinnerOverlay visible={payoutSpinner} title="Submitting payout request..." message="Validating your withdrawal eligibility" />
      <SpinnerOverlay visible={approvalSpinner} title="Processing approval..." message="Releasing funds to driver wallet" />
      <SpinnerOverlay visible={refundSpinner} title="Processing refund..." message="Crediting customer wallet" />
      {/* Success toast */}
      <SuccessToast
        visible={successToast?.visible}
        message={successToast?.message}
        subMessage={successToast?.subMessage}
        onDismiss={() => setSuccessToast(s => ({ ...s, visible: false }))}
        duration={3500}
        position="top"
      />
      {/* Top Nav */}
      <nav className="sticky top-0 z-40 bg-[#0a1628]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors"
                style={{ background: '#0f2044', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </button>
              <div className="w-8 h-8 rounded-lg bg-[#ffd700] flex items-center justify-center">
                <Wallet className="w-4 h-4 text-[#0a1628]" />
              </div>
              <div>
                <span className="text-white font-bold text-sm">OnTheGo</span>
                <span className="text-[#ffd700] font-bold text-sm"> Pay</span>
              </div>
            </div>

            {/* Desktop tabs */}
            <div className="hidden md:flex items-center gap-1 bg-white/5 rounded-xl p-1">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab?.id
                      ? 'bg-[#ffd700] text-[#0a1628]'
                      : 'text-white/50 hover:text-white/70'
                  }`}
                >
                  {tab?.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              {isAdmin && (
                <button
                  onClick={() => setShowRefundModal(true)}
                  className="hidden md:flex items-center gap-2 px-3 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 text-yellow-400 text-sm rounded-xl transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Refund
                </button>
              )}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#ffd700]/20 flex items-center justify-center">
                  <span className="text-[#ffd700] text-xs font-bold">
                    {userProfile?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="hidden sm:block">
                  <p className="text-white text-xs font-medium">{userProfile?.full_name || 'User'}</p>
                  <p className="text-white/40 text-xs capitalize">{userProfile?.role || 'customer'}</p>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"
              >
                {mobileMenuOpen ? <X className="w-4 h-4 text-white" /> : <Menu className="w-4 h-4 text-white" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-3 flex flex-wrap gap-2">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => { setActiveTab(tab?.id); setMobileMenuOpen(false); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab?.id ? 'bg-[#ffd700] text-[#0a1628]' : 'bg-white/5 text-white/50'
                  }`}
                >
                  {tab?.label}
                </button>
              ))}
              {isAdmin && (
                <button
                  onClick={() => { setShowRefundModal(true); setMobileMenuOpen(false); }}
                  className="px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 text-sm rounded-lg"
                >
                  Refund
                </button>
              )}
            </div>
          )}
        </div>
      </nav>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Security badge */}
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-4 h-4 text-[#ffd700]/60" />
          <p className="caption-text text-white/30">Ledger-based architecture · Balance computed from immutable transaction records · RLS enforced</p>
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Wallet Balance - spans 1 col */}
              <div className="lg:col-span-1">
                {loading ? (
                  <div className="space-y-3">
                    <SkeletonStatCard dark />
                    <SkeletonStatCard dark />
                  </div>
                ) : (
                  <WalletBalance
                    balance={balance}
                    pendingBalance={pendingBalance}
                    totalCredits={totalCredits}
                    totalDebits={totalDebits}
                    currency={currency}
                    loading={loading || balanceLoading}
                    onRefresh={handleRefresh}
                  />
                )}
              </div>

              {/* Earnings Chart - spans 2 cols */}
              <div className="lg:col-span-2">
                <EarningsChart transactions={transactions} currency={currency} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CommissionSummary transactions={transactions} currency={currency} />

              {/* Escrow Flow */}
              <div className="bg-[#0d1f3c] rounded-2xl border border-white/10 p-5">
                <h3 className="heading-3 text-white mb-4">Escrow Flow</h3>
                <div className="space-y-3">
                  {[
                    { step: '1', label: 'Customer pays fare', desc: 'Debit from customer wallet', color: 'bg-blue-500/20 text-blue-300' },
                    { step: '2', label: 'Platform escrow hold', desc: 'Credit to platform escrow wallet', color: 'bg-indigo-500/20 text-indigo-300' },
                    { step: '3', label: 'Trip completed', desc: 'Commission split calculated (20/80)', color: 'bg-purple-500/20 text-purple-300' },
                    { step: '4', label: 'Driver earnings released', desc: '80% credited to driver wallet', color: 'bg-[#ffd700]/20 text-[#ffd700]' },
                  ]?.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${item?.color}`}>
                        {item?.step}
                      </div>
                      <div className="flex-1">
                        <p className="body-text text-white/80 font-medium">{item?.label}</p>
                        <p className="caption-text text-white/30">{item?.desc}</p>
                      </div>
                      {i < 3 && <ArrowRight className="w-4 h-4 text-white/20 mt-1.5 flex-shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent transactions preview */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="heading-3 text-white">Recent Transactions</h3>
                <button
                  onClick={() => setActiveTab('transactions')}
                  className="text-[#ffd700]/60 text-sm hover:text-[#ffd700] transition-colors flex items-center gap-1"
                >
                  View all <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
              {loading ? (
                <SkeletonList count={4} dark />
              ) : (
                <TransactionHistory
                  transactions={transactions?.slice(0, 5)}
                  loading={false}
                  currency={currency}
                  onSelectTransaction={setSelectedTransaction}
                />
              )}
            </div>
          </div>
        )}

        {/* TRANSACTIONS TAB */}
        {activeTab === 'transactions' && (
          loading ? (
            <SkeletonList count={6} dark />
          ) : (
            <TransactionHistory
              transactions={transactions}
              loading={false}
              currency={currency}
              onSelectTransaction={setSelectedTransaction}
            />
          )
        )}

        {/* PAYOUT TAB */}
        {activeTab === 'payout' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PayoutRequestForm
              availableBalance={balance}
              currency={currency}
              walletId={wallet?.id}
              userId={user?.id}
              onSubmit={handlePayoutSubmit}
              loading={loading}
            />
            {loading ? (
              <SkeletonList count={3} dark />
            ) : (
              <AdminApprovalPanel
                payoutRequests={payoutRequests}
                loading={false}
                currency={currency}
                onApprove={handleApprove}
                onReject={handleReject}
                isAdmin={false}
              />
            )}
          </div>
        )}

        {/* ADMIN TAB */}
        {activeTab === 'admin' && isAdmin && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {loading ? (
                [0,1,2]?.map(i => <SkeletonStatCard key={i} dark />)
              ) : (
                [
                  { label: 'Pending Payouts', value: payoutRequests?.filter(r => r?.status === 'pending')?.length, color: 'text-yellow-400' },
                  { label: 'Approved Today', value: payoutRequests?.filter(r => r?.status === 'paid')?.length, color: 'text-emerald-400' },
                  { label: 'Total Requests', value: payoutRequests?.length, color: 'text-white' },
                ]?.map((stat, i) => (
                  <div key={i} className="bg-[#0d1f3c] rounded-2xl border border-white/10 p-5">
                    <p className="caption-text text-white/40 mb-2">{stat?.label}</p>
                    <p className={`heading-1 ${stat?.color}`}>{stat?.value}</p>
                  </div>
                ))
              )}
            </div>
            {loading ? (
              <SkeletonList count={4} dark />
            ) : (
              <AdminApprovalPanel
                payoutRequests={payoutRequests}
                loading={false}
                currency={currency}
                onApprove={handleApprove}
                onReject={handleReject}
                isAdmin={true}
              />
            )}
          </div>
        )}
      </main>
      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          currency={currency}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
      {/* Refund Modal */}
      {showRefundModal && (
        <RefundModal
          currency={currency}
          onClose={() => setShowRefundModal(false)}
          onSubmit={handleRefund}
        />
      )}
    </div>
  );
};

export default PaymentsWalletScreen;
